import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { MaskLines, Reveal, Marquee, SpotlightCard } from "../components/Motion";
import Seo from "../components/Seo";
import { Loading } from "../components/Sections";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Insights() {
  const { data, loading } = useApi(api.insights, []);

  if (loading || !data) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }

  const featured = data.articles.find((a) => a.slug === data.featured) || data.articles[0];
  const rest = data.articles.filter((a) => a.slug !== featured.slug);

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path="/insights" />

      <section className="relative tactical-grid border-b border-white/10" data-testid="insights-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink to-ink pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-12 sm:pb-16">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-amber" /> Intelligence Desk // UK Security Industry
          </p>
          <MaskLines
            lines={["SECURITY", "INSIGHTS"]}
            className="font-display font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-7xl text-white"
          />
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
            Field intelligence from the UK security industry: licensing, pricing, tactics and sector playbooks written
            by people who stand the post — not marketing departments.
          </p>
        </div>
      </section>

      <Marquee items={data.articles.map((a) => a.category + " Briefing")} />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="insights-index">
        <Reveal>
          <Link to={`/insights/${featured.slug}`} data-testid={`insight-featured-${featured.slug}`} className="block group">
            <SpotlightCard className="overflow-hidden hover:border-amber/60 transition-colors duration-300">
              <div className="relative h-56 sm:h-72 overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest bg-amber text-ink px-3 py-1.5">
                  This Week's Briefing
                </span>
              </div>
              <div className="p-8 sm:p-12 pt-6 sm:pt-8">
                <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  <span className="text-amber border border-amber/40 px-2.5 py-1">{featured.category}</span>
                  <span>{fmtDate(featured.date)}</span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {featured.minutes} min read
                  </span>
                </div>
                <h2 className="mt-6 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl text-white group-hover:text-amber transition-colors leading-tight max-w-3xl">
                  {featured.title}
                </h2>
                <p className="mt-4 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
                  Read briefing <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </SpotlightCard>
          </Link>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.06}>
              <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="block h-full group">
                <SpotlightCard className="h-full overflow-hidden hover:border-amber/60 transition-colors duration-300 flex flex-col">
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                  </div>
                  <div className="p-6 pt-4 flex flex-col flex-1">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      <span className="text-amber">{a.category}</span>
                      <span>{fmtDate(a.date)}</span>
                    </div>
                    <h3 className="mt-4 font-display font-bold uppercase tracking-wide text-lg sm:text-xl text-white group-hover:text-amber transition-colors leading-tight">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-xs sm:text-sm text-gray-400 leading-relaxed flex-1">{a.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> {a.minutes} min
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-amber group-hover:rotate-45 transition-transform duration-300" />
                    </div>
                  </div>
                </SpotlightCard>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
